import { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Play, CheckCircle2, XCircle, Trash2, Pencil, Users, Target,
  Award, Clock, BarChart3, List, LayoutGrid, History, CalendarRange,
} from 'lucide-react';

import { StatusBadge } from '@/components/shared/StatusBadge';
import { StatCard } from '@/components/shared/StatCard';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { sprintService, type SprintDetail } from '@/services/sprint.service';
import { taskService, type Task } from '@/services/task.service';

import { SprintListView } from '@/components/sprints/SprintListView';
import { SprintKanbanView } from '@/components/sprints/SprintKanbanView';
import { SprintGoalsPanel } from '@/components/sprints/SprintGoalsPanel';
import { SprintTeamDialog } from '@/components/sprints/SprintTeamDialog';
import { SprintEvaluatorsDialog } from '@/components/sprints/SprintEvaluatorsDialog';
import { EditSprintDialog } from '@/components/sprints/EditSprintDialog';
import { CreateTaskDialog } from '@/components/sprints/CreateTaskDialog';
import { SprintDateHistoryDialog } from '@/components/sprints/SprintDateHistoryDialog';
import { TasksPlanner } from '@/components/sprints/TasksPlanner';
import { UnifiedTaskDetailsDialog } from '@/components/tasks/UnifiedTaskDetailsDialog';
import { TaskPlannerView } from '@/components/planner/TaskPlannerView';

// ── Display status helper ──
function getDisplayStatus(sprint: SprintDetail): string {
  if (sprint.status === 'completed') return 'completed';
  if (sprint.status === 'cancelled') return 'cancelled';
  if (sprint.is_late) return 'late';
  if (sprint.is_live) return 'live';
  return sprint.status; // planning or active
}

function getDaysRemaining(sprint: SprintDetail): string {
  const now = new Date();
  const end = new Date(sprint.end_date);
  if (end < now) return 'Ended';
  const days = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return `${days}d`;
}

export function SprintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTab = (location.state as any)?.fromTab || 'sprints-list';
  const backUrl = `/work?tab=${backTab}`;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();

  // ── View state ──
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'planner'>(() => {
    try { return (localStorage.getItem('sprint-view-mode') as 'list' | 'kanban' | 'planner') || 'list'; } catch { return 'list'; }
  });
  const [activeTab, setActiveTab] = useState<'tasks' | 'goals'>('tasks');
  const [confirmOpen, setConfirmOpen] = useState<string | null>(null);

  // ── Dialog state ──
  const [teamOpen, setTeamOpen] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [createTaskColumn, setCreateTaskColumn] = useState<{ id: string; name: string } | null>(null);
  const [plannerColumn, setPlannerColumn] = useState<{ id: string; name: string } | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);

  // ── Queries ──
  const { data: sprint, isLoading } = useQuery({
    queryKey: ['sprint', id],
    queryFn: () => sprintService.get(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['sprint-tasks', id],
    queryFn: () => taskService.list({ sprint_id: id!, limit: 500 }).then((r) => r.data),
    enabled: !!id,
  });

  const sprintTasks: Task[] = tasksData?.data ?? [];

  // ── Permissions ──
  const userMember = sprint?.members?.find((m) => m.user?.id === user?.id);
  const isOwner = userMember?.role === 'owner' || sprint?.owner_id === user?.id;
  const isManager = userMember?.role === 'manager' || isOwner;
  const canManage = isManager || isAdmin;
  const isLocked = sprint?.status === 'completed' || sprint?.status === 'cancelled';

  // Column leader check
  const userLeaderColumns = useMemo(() => {
    if (!sprint || !user) return new Set<string>();
    const cols = new Set<string>();
    for (const col of sprint.columns) {
      for (const leader of col.leaders || []) {
        if (leader.user?.id === user.id) cols.add(col.id);
      }
    }
    return cols;
  }, [sprint, user]);

  function canManageColumn(columnId: string): boolean {
    return canManage || userLeaderColumns.has(columnId);
  }

  // ── Mutations ──
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['sprint', id] });
    queryClient.invalidateQueries({ queryKey: ['sprint-tasks', id] });
    queryClient.invalidateQueries({ queryKey: ['work-sprints'] });
  }

  const activateMut = useMutation({
    mutationFn: () => sprintService.activate(id!),
    onSuccess: () => { invalidate(); toast({ title: 'Sprint activated' }); setConfirmOpen(null); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });
  const completeMut = useMutation({
    mutationFn: () => sprintService.complete(id!),
    onSuccess: () => { invalidate(); toast({ title: 'Sprint completed' }); setConfirmOpen(null); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });
  const cancelMut = useMutation({
    mutationFn: () => sprintService.cancel(id!),
    onSuccess: () => { invalidate(); toast({ title: 'Sprint cancelled' }); setConfirmOpen(null); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });
  const deleteMut = useMutation({
    mutationFn: () => sprintService.delete(id!),
    onSuccess: () => { toast({ title: 'Sprint deleted' }); navigate(backUrl); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function setView(mode: 'list' | 'kanban' | 'planner') {
    setViewMode(mode);
    try { localStorage.setItem('sprint-view-mode', mode); } catch {}
  }

  // ── Loading / Not Found ──
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!sprint) {
    return <EmptyState title="Sprint not found" description="This sprint may have been deleted." />;
  }

  const stats = sprint.stats ?? { total_tasks: 0, done_tasks: 0, in_progress_tasks: 0, not_started_tasks: 0 };
  const progress = stats.total_tasks > 0 ? Math.round((stats.done_tasks / stats.total_tasks) * 100) : 0;
  const displayStatus = getDisplayStatus(sprint);
  const daysRemaining = getDaysRemaining(sprint);
  const totalEst = sprintTasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);
  const totalAct = sprintTasks.reduce((s, t) => s + (t.actual_hours || 0), 0);
  const uniqueAssignees = new Set(sprintTasks.filter((t) => t.assignee_id).map((t) => t.assignee_id)).size;
  const goalCount = sprint.sprint_goals?.length || 0;
  const evaluators = sprint.evaluators || [];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <button
          type="button"
          onClick={() => navigate(backUrl)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Work Planning
        </button>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs bg-muted text-muted-foreground px-2 py-1 rounded">{sprint.reference_id}</span>
              <StatusBadge status={displayStatus} />
              <h1 className="text-2xl font-bold">{sprint.name}</h1>
            </div>
            {sprint.description && (
              <p className="mt-1 text-sm text-muted-foreground">{sprint.description}</p>
            )}

            {/* Quick action links */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <button
                type="button"
                onClick={() => setActiveTab('goals')}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Target className="h-4 w-4" />
                Goals ({goalCount})
              </button>
              <button
                type="button"
                onClick={() => setTeamOpen(true)}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Users className="h-4 w-4" />
                Sprint Team ({sprint.members?.length || 0}{' '}
                {sprint.members?.filter((m) => m.role === 'manager' || m.role === 'owner').length
                  ? `- ${sprint.members.filter((m) => m.role === 'manager' || m.role === 'owner').length} mgr`
                  : ''})
              </button>
              <button
                type="button"
                onClick={() => setEvalOpen(true)}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Award className="h-4 w-4" />
                Evaluation Team ({evaluators.length})
              </button>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Status action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {canManage && sprint.status === 'planning' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setConfirmOpen('activate')}>
                <Play className="mr-1 h-4 w-4" /> Activate
              </Button>
            )}
            {canManage && sprint.status === 'active' && (
              <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark" onClick={() => setConfirmOpen('complete')}>
                <CheckCircle2 className="mr-1 h-4 w-4" /> Complete Sprint
              </Button>
            )}
            {canManage && (sprint.status === 'active' || sprint.status === 'planning') && (
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen('cancel')}>
                <XCircle className="mr-1 h-4 w-4" /> Cancel
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirmOpen('delete')}>
                <Trash2 className="mr-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Progress" value={`${progress}%`} color="green" />
        <StatCard
          icon={CheckCircle2}
          label={`Tasks — ${stats.in_progress_tasks} in progress · ${totalEst.toFixed(1)}h est / ${totalAct.toFixed(1)}h actual`}
          value={`${stats.done_tasks}/${stats.total_tasks}`}
          color="blue"
        />
        <div
          className="flex items-center gap-3 p-4 rounded-xl bg-background/50 backdrop-blur-sm border border-border hover:bg-background/70 transition-all cursor-pointer"
          onClick={() => setHistoryOpen(true)}
        >
          <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-600">
            <Clock className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold">{daysRemaining}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(sprint.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' — '}
              {new Date(sprint.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          </div>
          <button type="button" className="text-xs text-brand-primary hover:underline">
            <History className="h-4 w-4" />
          </button>
        </div>
        <StatCard icon={Users} label={`${goalCount} sprint goals`} value={`${uniqueAssignees}`} color="purple" />
      </div>

      {/* ── Tab switcher ── */}
      <div className="flex items-center justify-between border-b">
        <div className="flex">
          <button
            type="button"
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tasks'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Tasks
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('goals')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'goals'
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Sprint Goals
          </button>
        </div>

        {activeTab === 'tasks' && (
          <div className="flex items-center gap-1 pb-1">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8"
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8"
              onClick={() => setView('kanban')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'planner' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8"
              onClick={() => setView('planner')}
            >
              <CalendarRange className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      {activeTab === 'tasks' && viewMode === 'list' && (
        <SprintListView
          sprint={sprint}
          tasks={sprintTasks}
          canManageColumn={canManageColumn}
          isLocked={isLocked}
          onCreateTask={(colId, colName) => setCreateTaskColumn({ id: colId, name: colName })}
          onOpenPlanner={(colId, colName) => setPlannerColumn({ id: colId, name: colName })}
          onViewTask={(taskId) => setViewTaskId(taskId)}
        />
      )}
      {activeTab === 'tasks' && viewMode === 'kanban' && (
        <SprintKanbanView
          sprint={sprint}
          tasks={sprintTasks}
          canManageColumn={canManageColumn}
          isLocked={isLocked}
          onCreateTask={(colId, colName) => setCreateTaskColumn({ id: colId, name: colName })}
          onViewTask={(taskId) => setViewTaskId(taskId)}
        />
      )}
      {activeTab === 'tasks' && viewMode === 'planner' && (
        <TaskPlannerView
          tasks={sprintTasks}
          onTaskClick={(taskId) => setViewTaskId(taskId)}
        />
      )}

      {activeTab === 'goals' && (
        <SprintGoalsPanel
          sprintId={id!}
          goals={sprint.sprint_goals || []}
          canManage={canManage}
          isLocked={isLocked}
          groupId={sprint.group_id}
        />
      )}

      {/* ── Dialogs ── */}
      <SprintTeamDialog
        open={teamOpen}
        onOpenChange={setTeamOpen}
        sprintId={id!}
        groupId={sprint.group_id}
        members={sprint.members || []}
        canManage={canManage}
      />

      <SprintEvaluatorsDialog
        open={evalOpen}
        onOpenChange={setEvalOpen}
        sprintId={id!}
        groupId={sprint.group_id}
        evaluators={evaluators}
        canManage={canManage}
      />

      {editOpen && (
        <EditSprintDialog open={editOpen} onOpenChange={setEditOpen} sprint={sprint} />
      )}

      {historyOpen && (
        <SprintDateHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} sprint={sprint} />
      )}

      {createTaskColumn && (
        <CreateTaskDialog
          open={!!createTaskColumn}
          onOpenChange={(v) => !v && setCreateTaskColumn(null)}
          sprintId={id!}
          columnId={createTaskColumn.id}
          columnName={createTaskColumn.name}
          members={sprint.members || []}
          sprintGoals={sprint.sprint_goals || []}
        />
      )}

      {plannerColumn && (
        <TasksPlanner
          open={!!plannerColumn}
          onOpenChange={(v) => !v && setPlannerColumn(null)}
          sprintId={id!}
          columnId={plannerColumn.id}
          columnName={plannerColumn.name}
          sprint={sprint}
        />
      )}

      <UnifiedTaskDetailsDialog
        open={!!viewTaskId}
        onOpenChange={(v) => !v && setViewTaskId(null)}
        taskId={viewTaskId}
        canEditOverride={canManage}
        canDeleteOverride={canManage}
        onDeleted={invalidate}
        members={sprint?.members?.map((m) => ({ id: m.user?.id || m.id, name: m.user?.full_name || 'Unknown' })) ?? []}
        sprintGoals={sprint?.sprint_goals?.map((sg) => ({ id: sg.id, title: sg.title })) ?? []}
        columns={sprint?.columns?.map((c) => ({ id: c.id, name: c.name })) ?? []}
      />

      {/* Confirmation dialogs */}
      <ConfirmDialog
        open={confirmOpen === 'activate'}
        onOpenChange={() => setConfirmOpen(null)}
        title="Activate Sprint"
        description="Move this sprint from Planning to Active? This will allow task work to begin."
        confirmLabel="Activate"
        isLoading={activateMut.isPending}
        onConfirm={() => activateMut.mutate()}
      />
      <ConfirmDialog
        open={confirmOpen === 'complete'}
        onOpenChange={() => setConfirmOpen(null)}
        title="Complete Sprint"
        description="Mark this sprint as completed? Ensure all tasks are done before completing."
        confirmLabel="Complete Sprint"
        isLoading={completeMut.isPending}
        onConfirm={() => completeMut.mutate()}
      />
      <ConfirmDialog
        open={confirmOpen === 'cancel'}
        onOpenChange={() => setConfirmOpen(null)}
        title="Cancel Sprint"
        description="Cancel this sprint? This cannot be undone."
        confirmLabel="Cancel Sprint"
        variant="destructive"
        isLoading={cancelMut.isPending}
        onConfirm={() => cancelMut.mutate()}
      />
      <ConfirmDialog
        open={confirmOpen === 'delete'}
        onOpenChange={() => setConfirmOpen(null)}
        title="Delete Sprint"
        description={`Permanently delete "${sprint.name}"? All tasks in this sprint will be removed.`}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
      />
    </div>
  );
}
